/**
 * Utilitário para padronização de estilos de formulários no módulo de fossas sépticas
 * Este arquivo centraliza todos os estilos de inputs, selects e outros elementos de formulário
 */

/**
 * Estilo base para campos de formulário (input, select, textarea)
 */
const baseFieldStyle = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  borderRadius: '4px',
  border: '1px solid #ced4da',
  transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  backgroundColor: 'white',
  outline: 'none'
};

/**
 * Estilo para campos de formulário com foco
 */
const focusFieldStyle = {
  borderColor: '#2e7d32',
  boxShadow: '0 0 0 0.2rem rgba(46, 125, 50, 0.25)'
};

/**
 * Estilo para campos de formulário com erro
 */
const errorFieldStyle = {
  borderColor: '#dc3545',
  backgroundColor: '#fff8f8'
};

/**
 * Estilo para mensagens de erro
 */
export const errorMessageStyle = {
  color: '#dc3545',
  fontSize: '14px',
  marginTop: '5px'
};

/**
 * Estilo para labels de formulário
 */
export const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#333'
};

/**
 * Estilo para texto de ajuda (small)
 */
export const helpTextStyle = {
  display: 'block',
  marginTop: '5px',
  color: '#6c757d',
  fontSize: '12px'
};

/**
 * Estilo para grupo de formulário
 */
export const formGroupStyle = {
  marginBottom: '15px'
};

/**
 * Estilo para input
 * @param {Object} options - Opções de estilo (error, disabled)
 * @returns {Object} - Estilo completo do input
 */
export const getInputStyle = (options = {}) => {
  const { error = false, disabled = false } = options;

  let style = { ...baseFieldStyle };

  if (error) {
    style = { ...style, ...errorFieldStyle };
  }

  if (disabled) {
    style = {
      ...style,
      backgroundColor: '#e9ecef',
      cursor: 'not-allowed',
      opacity: 0.7
    };
  }

  return style;
};

/**
 * Estilo para select
 * @param {Object} options - Opções de estilo (error, disabled)
 * @returns {Object} - Estilo completo do select
 */
export const getSelectStyle = (options = {}) => {
  const { error = false, disabled = false } = options;

  let style = {
    ...baseFieldStyle,
    appearance: 'none', // Alterado de 'auto' para 'none' para ocultar a seta padrão do navegador
    backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3e%3cpath fill=\'none\' stroke=\'%23343a40\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M2 5l6 6 6-6\'/%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.75rem center',
    backgroundSize: '16px 12px',
    paddingRight: '2.5rem'
  };

  if (error) {
    style = { ...style, ...errorFieldStyle };
  }

  if (disabled) {
    style = {
      ...style,
      backgroundColor: '#e9ecef',
      cursor: 'not-allowed',
      opacity: 0.7
    };
  }

  return style;
};

/**
 * Estilo para textarea
 * @param {Object} options - Opções de estilo (error, disabled, height)
 * @returns {Object} - Estilo completo do textarea
 */
export const getTextareaStyle = (options = {}) => {
  const { error = false, disabled = false, height = '100px' } = options;

  let style = {
    ...baseFieldStyle,
    minHeight: height,
    resize: 'vertical'
  };

  if (error) {
    style = { ...style, ...errorFieldStyle };
  }

  if (disabled) {
    style = {
      ...style,
      backgroundColor: '#e9ecef',
      cursor: 'not-allowed',
      opacity: 0.7
    };
  }

  return style;
};

/**
 * Estilo para grid de formulário
 * @param {number} columns - Número de colunas
 * @returns {Object} - Estilo para o grid
 */
export const getFormGridStyle = (columns = 2) => {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: '15px'
  };
};

/**
 * Estilo para container de formulário
 */
export const formContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '20px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

/**
 * Estilo para ações de formulário (botões)
 */
export const formActionsStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  marginTop: '20px'
};

export default {
  getInputStyle,
  getSelectStyle,
  getTextareaStyle,
  getFormGridStyle,
  labelStyle,
  errorMessageStyle,
  helpTextStyle,
  formGroupStyle,
  formContainerStyle,
  formActionsStyle
};